import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {FormControl, Validators} from '@angular/forms';
import {environment} from '@env/environment';
import {RegexpEnum} from '@enums/regexp.enum';
import {LocalStorageService} from '@services/local-storage.service';
import {
  CONNECT_AI_PATH,
  CONNECT_IDENTIFY_NEXT,
  CONNECT_SIGN_IN_MESSAGES,
  CONNECTOR_MESSAGES,
  CONNECTOR_REASON,
  EXTERNAL_AUTH_ID_MAX_LENGTH,
  EXTERNAL_AUTH_ID_PARAM,
  LOGIN_HINT_PARAM,
} from './connect-ai.model';

/**
 * What the connect version of the login page needs (card #1310). It exists only when the login `ref`
 * points at `/connect-ai`.
 */
export interface ConnectAiLoginOptions {
  /** The `ref` query value exactly as received, handed on unchanged to the reset password page. */
  ref: string;
  /** The `/connect-ai?...` URL the `ref` decodes to, where the sign-in, and a sign-up started here, come back to. */
  returnUrl: string;
  externalAuthId: string | null;
  portalUrl: string | null;
}

/** What the password screen of the connect sign-in shows after a failed sign-in. */
export interface ConnectPasswordError {
  message: string;
  /** True when the login is locked after 3 wrong passwords: the message then links to the password reset. */
  locked: boolean;
}

/**
 * A sign-up that started on the connect sign-in (card #1310). It is kept in this browser's localStorage, because the
 * Zoho payment comes back to the fixed sign-up completion page, which uses it to return to the connect page.
 */
export interface ConnectAiSignUp {
  /** The `/connect-ai?...` URL the person came from, which they go back to after paying. */
  returnUrl: string;
  /** The email or username the person typed on the connect sign-in, or the email they registered with. */
  identifier: string;
  /** The account's username, set only once the account exists: never after a refused registration. */
  username: string | null;
  createdAt: number;
  /** When the payment came back to the sign-up completion page. */
  paidAt: number | null;
}

const RESET_MARKER_KEY = 'connect-ai.password-reset-requested-at';
const RESET_MARKER_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const SIGN_UP_MARKER_KEY = 'connect-ai.sign-up';
const SIGN_UP_MARKER_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** The key the normal registration writes the new username to, and only when the registration succeeded. */
const REGISTERED_USERNAME_KEY = 'username';
/** The sign-in answer for an account whose sign-up was never paid (prod only). */
const UNPAID_REGISTRATION_CODE = 426;
/** The sign-in answer for an account that has paid but whose bank data setup never finished. */
const UNFINISHED_SETUP_CODE = 417;

/** The server's wording for a wrong username or password, and for a login locked after 3 wrong passwords. */
const SERVER_BAD_CREDENTIALS = 'Bad credentials';
const SERVER_LOCKED_PREFIX = 'Your login has been locked due to typing in a password incorrectly';

/** The page message for a reason code; an unknown or missing reason gets the CONNECT_FAILED message. */
export function connectorMessage(reason: string | null | undefined): string {
  return (reason && CONNECTOR_MESSAGES[reason]) || CONNECTOR_MESSAGES[CONNECTOR_REASON.CONNECT_FAILED];
}

/** The `reason` of a `{"reason": "<CODE>"}` error body, or null for any other body. */
export function reasonFromError(error: HttpErrorResponse | null | undefined): string | null {
  const reason = error?.error?.reason;
  return typeof reason === 'string' && reason ? reason : null;
}

/** Decodes a login `ref` value the same way the login page does, returning null when it is absent or malformed. */
export function decodeReturnUrl(rawRef: string | null | undefined): string | null {
  if (!rawRef) {
    return null;
  }
  try {
    return decodeURIComponent(rawRef);
  } catch {
    return null;
  }
}

/** True when an in-app URL is the connect page itself, with or without a query string. */
export function isConnectAiUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }
  const path = url.split(/[?#]/)[0];
  return path === CONNECT_AI_PATH;
}

export function isUsableExternalAuthId(externalAuthId: string | null | undefined): boolean {
  return !!externalAuthId && externalAuthId.trim().length > 0 && externalAuthId.length <= EXTERNAL_AUTH_ID_MAX_LENGTH;
}

/** The `external_auth_id` of a `/connect-ai?...` URL, or null. */
export function externalAuthIdFromUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url, 'https://app.invalid').searchParams.get(EXTERNAL_AUTH_ID_PARAM);
  } catch {
    return null;
  }
}

/** The developer portal's email sign-in, for an email whose LedgerSync logins are developer accounts. */
export function portalConnectUrl(externalAuthId: string): string {
  return `${environment.PORTAL_BASE_URL}/connect-ai?${EXTERNAL_AUTH_ID_PARAM}=${encodeURIComponent(externalAuthId)}`
    + `&deployment=${encodeURIComponent(environment.CONNECTOR_DEPLOYMENT)}`;
}

/** The developer portal's email sign-in with the email the person already typed, so it is filled in for them. */
export function portalConnectUrlWithEmail(portalUrl: string, email: string): string {
  return `${portalUrl}&${LOGIN_HINT_PARAM}=${encodeURIComponent(email)}`;
}

/**
 * The only place the connect page may send the browser: an https URL on exactly the configured connector
 * AuthKit host. A blank host allows nothing.
 */
export function isAllowedRedirect(uri: string | null | undefined, allowedHost: string | null | undefined): boolean {
  if (!uri || !allowedHost) {
    return false;
  }
  try {
    const url = new URL(uri);
    return url.protocol === 'https:'
      && !url.username
      && !url.password
      && url.host.toLowerCase() === allowedHost.trim().toLowerCase();
  } catch {
    return false;
  }
}

/** Builds the connect version of the login page from the login `ref`, or null for a normal sign-in. */
export function connectAiLoginOptions(rawRef: string | null | undefined): ConnectAiLoginOptions | null {
  const returnUrl = decodeReturnUrl(rawRef);
  if (!isConnectAiUrl(returnUrl)) {
    return null;
  }
  const externalAuthId = externalAuthIdFromUrl(returnUrl);
  const usable = isUsableExternalAuthId(externalAuthId);
  return {
    ref: rawRef,
    returnUrl,
    externalAuthId: usable ? externalAuthId : null,
    portalUrl: usable ? portalConnectUrl(externalAuthId) : null,
  };
}

/**
 * True for the sign-in answer of an account whose sign-up was never paid (426), which a normal login sends to the
 * payment page. The connect version shows its Finish step instead, or the wait message right after a payment.
 */
export function isUnpaidRegistrationError(error: HttpErrorResponse | null | undefined): boolean {
  return error?.error?.code === UNPAID_REGISTRATION_CODE || error?.status === UNPAID_REGISTRATION_CODE;
}

/** The username a 426 sign-in answer names (the normal login envelope carries it), or null. */
export function unpaidRegistrationUsername(error: HttpErrorResponse | null | undefined): string | null {
  const username = error?.error?.username;
  return typeof username === 'string' && username.trim() ? username.trim() : null;
}

/**
 * True for the sign-in answer of a paid account whose setup never finished (417), which a normal login sends to the
 * registration completion page. The connect version shows the SETUP_NOT_FINISHED message instead.
 */
export function isUnfinishedSetupError(error: HttpErrorResponse | null | undefined): boolean {
  return error?.error?.code === UNFINISHED_SETUP_CODE || error?.status === UNFINISHED_SETUP_CODE;
}

/**
 * Remembers that a password reset was asked for from the connect version of the login page. Same browser only:
 * a reset finished on another device shows the normal page, and the reset email itself (reset_password.ftl,
 * aiAssistantNotice) is what tells the owner to connect again in Claude or ChatGPT.
 */
export function markConnectAiPasswordReset(storage: LocalStorageService, now: number = Date.now()): void {
  storage.setValue(RESET_MARKER_KEY, String(now));
}

/** True, once, when the reset being finished was asked for from the connect login page in the last 24 hours. */
export function takeConnectAiPasswordReset(storage: LocalStorageService, now: number = Date.now()): boolean {
  const requestedAt = Number(storage.getValue(RESET_MARKER_KEY));
  storage.removeItem(RESET_MARKER_KEY);
  return requestedAt > 0 && now - requestedAt >= 0 && now - requestedAt <= RESET_MARKER_MAX_AGE_MS;
}

/** The identify answers that keep the person on the email-or-username screen, with the message they see there. */
const IDENTIFY_STOP_MESSAGES: Readonly<Record<string, string>> = {
  [CONNECT_IDENTIFY_NEXT.NOT_FOUND]: CONNECT_SIGN_IN_MESSAGES.NOT_FOUND,
  [CONNECT_IDENTIFY_NEXT.USERNAME]: CONNECT_SIGN_IN_MESSAGES.USERNAME,
  [CONNECT_IDENTIFY_NEXT.CLOSED]: CONNECT_SIGN_IN_MESSAGES.CLOSED,
  [CONNECT_IDENTIFY_NEXT.WORK_EMAIL]: CONNECT_SIGN_IN_MESSAGES.WORK_EMAIL,
};

/** The message under the email-or-username input for an identify answer that does not move on. */
export function identifyStopMessage(next: string | null | undefined): string {
  return (next && IDENTIFY_STOP_MESSAGES[next]) || CONNECT_SIGN_IN_MESSAGES.FAILED;
}

/** The normal sign-up form's own email rule (RegisterUserComponent), only read here. */
const SIGN_UP_EMAIL_RULE = Validators.pattern(RegexpEnum.email);
const PLUS_SIGN = '+';

/**
 * The message for an email the normal sign-up form would refuse, so the connect sign-in never offers to create an
 * account with it. It runs that form's own email rule on the trimmed email, so the two cannot disagree. Null when the
 * form accepts the email.
 */
export function signUpEmailRefusal(email: string | null | undefined): string | null {
  const trimmed = (email || '').trim();
  if (!SIGN_UP_EMAIL_RULE(new FormControl(trimmed))) {
    return null;
  }
  return trimmed.includes(PLUS_SIGN) ? CONNECT_SIGN_IN_MESSAGES.PLUS_EMAIL : CONNECT_SIGN_IN_MESSAGES.EMAIL_FORMAT;
}

/**
 * The message for a refused call to either connector sign-in endpoint: too many tries (429), the sign-in switched
 * off or not deployed yet (404, as on the Allow page), an account support has to check (409 ACCOUNT_CHECK, not a
 * retry), or anything else.
 */
export function connectRequestErrorMessage(error: HttpErrorResponse | null | undefined): string {
  const reason = reasonFromError(error);
  if (reason === CONNECTOR_REASON.TOO_MANY_ATTEMPTS || error?.status === HttpStatusCode.TooManyRequests) {
    return CONNECT_SIGN_IN_MESSAGES.TOO_MANY_ATTEMPTS;
  }
  if (reason === CONNECTOR_REASON.ACCOUNT_CHECK) {
    return connectorMessage(CONNECTOR_REASON.ACCOUNT_CHECK);
  }
  if (reason === CONNECTOR_REASON.NOT_AVAILABLE || error?.status === HttpStatusCode.NotFound) {
    return connectorMessage(CONNECTOR_REASON.NOT_AVAILABLE);
  }
  return CONNECT_SIGN_IN_MESSAGES.FAILED;
}

/**
 * What the password screen shows after a failed sign-in. The sign-in answers like the normal login, so a wrong
 * password and a locked login carry the server's own wording, which this turns into the page's short messages.
 * Any other server message is shown as it is. A connector refusal (`{"reason": ...}`) is never a login answer.
 */
export function connectPasswordError(error: HttpErrorResponse | null | undefined): ConnectPasswordError {
  const serverMessage = error?.error?.message;
  if (reasonFromError(error) || typeof serverMessage !== 'string' || !serverMessage) {
    return {message: connectRequestErrorMessage(error), locked: false};
  }
  if (serverMessage === SERVER_BAD_CREDENTIALS) {
    return {message: CONNECT_SIGN_IN_MESSAGES.WRONG_PASSWORD, locked: false};
  }
  if (serverMessage.startsWith(SERVER_LOCKED_PREFIX)) {
    return {message: CONNECT_SIGN_IN_MESSAGES.LOCKED, locked: true};
  }
  return {message: serverMessage, locked: false};
}

/**
 * The connect version of the login page for a connect page URL. The `ref` is encoded twice, as AuthenticatedFilter
 * does, because the login page decodes it once more after the router has.
 */
export function connectAiLoginUrl(returnUrl: string): string {
  return `/login?ref=${encodeURIComponent(encodeURIComponent(returnUrl))}`;
}

/** True when two usernames or emails are the same login, which the server compares ignoring case. */
function sameLogin(first: string | null | undefined, second: string | null | undefined): boolean {
  return !!first?.trim() && !!second?.trim() && first.trim().toLowerCase() === second.trim().toLowerCase();
}

function isWithinMarkerAge(time: unknown, now: number): time is number {
  return typeof time === 'number' && Number.isFinite(time) && now - time >= 0
    && now - time <= SIGN_UP_MARKER_MAX_AGE_MS;
}

/** The stored connect sign-up, or null for anything that is not one. */
function parseConnectAiSignUp(raw: string): ConnectAiSignUp | null {
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object' || typeof value.returnUrl !== 'string' || !isConnectAiUrl(value.returnUrl)
      || typeof value.identifier !== 'string' || !value.identifier.trim() || typeof value.createdAt !== 'number') {
      return null;
    }
    return {
      returnUrl: value.returnUrl,
      identifier: value.identifier.trim(),
      username: typeof value.username === 'string' && value.username.trim() ? value.username.trim() : null,
      createdAt: value.createdAt,
      paidAt: typeof value.paidAt === 'number' ? value.paidAt : null,
    };
  } catch {
    return null;
  }
}

function writeConnectAiSignUp(storage: LocalStorageService, signUp: ConnectAiSignUp): void {
  try {
    storage.setValue(SIGN_UP_MARKER_KEY, JSON.stringify(signUp));
  } catch {
    // Storage full or blocked: the sign-up goes on as a normal one and ends on the app's home page.
  }
}

/** Forgets this browser's connect sign-up. */
export function clearConnectAiSignUp(storage: LocalStorageService): void {
  try {
    storage.removeItem(SIGN_UP_MARKER_KEY);
  } catch {
    // Storage blocked: there is nothing to forget.
  }
}

/** This browser's connect sign-up, or null when there is none or it started more than 24 hours ago. */
export function readConnectAiSignUp(storage: LocalStorageService, now: number = Date.now()): ConnectAiSignUp | null {
  let raw: string | null;
  try {
    raw = storage.getValue(SIGN_UP_MARKER_KEY);
  } catch {
    return null;
  }
  if (!raw) {
    return null;
  }
  const signUp = parseConnectAiSignUp(raw);
  if (!signUp || !isWithinMarkerAge(signUp.createdAt, now)) {
    clearConnectAiSignUp(storage);
    return null;
  }
  return signUp;
}

/** "Create account" on the connect sign-in: remembers the connect page to come back to after the normal sign-up. */
export function startConnectAiSignUp(storage: LocalStorageService, returnUrl: string, email: string,
                                     now: number = Date.now()): void {
  writeConnectAiSignUp(storage, {returnUrl, identifier: email.trim(), username: null, createdAt: now, paidAt: null});
}

/**
 * "Finish" for an account whose sign-up was never paid: remembers the connect page to come back to after paying and,
 * as the normal login does for such an account, stores the username the payment page is for.
 */
export function startConnectAiFinish(storage: LocalStorageService, returnUrl: string, identifier: string,
                                     username: string, now: number = Date.now()): void {
  writeConnectAiSignUp(storage, {returnUrl, identifier: identifier.trim(), username, createdAt: now, paidAt: null});
  try {
    storage.setValue(REGISTERED_USERNAME_KEY, username);
  } catch {
    // Storage blocked: the payment page then fails exactly as it does for the normal login.
  }
}

/**
 * The connect sign-up form is sending this email: the sign-up follows it, with no account recorded yet. A username
 * an earlier registration or login left in this browser is forgotten first, so only this registration's success can
 * record one. An account already recorded for this same email (the person came back to the form) stays as it is.
 */
export function setConnectAiSignUpEmail(storage: LocalStorageService, email: string | null | undefined): void {
  const signUp = readConnectAiSignUp(storage);
  const typed = (email || '').trim();
  if (!signUp || !typed || (signUp.username && sameLogin(signUp.identifier, typed))) {
    return;
  }
  try {
    storage.removeItem(REGISTERED_USERNAME_KEY);
  } catch {
    // Storage blocked: nothing was stored either.
  }
  writeConnectAiSignUp(storage, {...signUp, identifier: typed, username: null, paidAt: null});
}

/**
 * Records the account of the connect sign-up once the normal registration created it. That registration stores the
 * new username under 'username' only when it succeeds, so a refused one (email already taken, blocked domain) never
 * names an account here.
 */
export function recordConnectAiSignUpAccount(storage: LocalStorageService,
                                             now: number = Date.now()): ConnectAiSignUp | null {
  const signUp = readConnectAiSignUp(storage, now);
  if (!signUp || signUp.username) {
    return signUp;
  }
  let registered: string | null;
  try {
    registered = storage.getValue(REGISTERED_USERNAME_KEY);
  } catch {
    return signUp;
  }
  if (!sameLogin(registered, signUp.identifier)) {
    return signUp;
  }
  const recorded: ConnectAiSignUp = {...signUp, username: registered.trim()};
  writeConnectAiSignUp(storage, recorded);
  return recorded;
}

/**
 * The sign-up completion page, back from the Zoho payment: when this browser's connect sign-up is for this username,
 * it notes the payment and answers the connect page to go back to. Null for any other sign-up, which then goes on
 * exactly as before.
 */
export function connectAiReturnAfterPayment(storage: LocalStorageService, username: string | null | undefined,
                                            now: number = Date.now()): string | null {
  const signUp = recordConnectAiSignUpAccount(storage, now);
  if (!signUp || !sameLogin(signUp.username, username)) {
    return null;
  }
  writeConnectAiSignUp(storage, {...signUp, paidAt: now});
  return signUp.returnUrl;
}

/** This browser's connect sign-up that started from this connect link, or null. */
export function connectAiSignUpFor(storage: LocalStorageService, externalAuthId: string | null | undefined,
                                   now: number = Date.now()): ConnectAiSignUp | null {
  const signUp = externalAuthId ? readConnectAiSignUp(storage, now) : null;
  return signUp && externalAuthIdFromUrl(signUp.returnUrl) === externalAuthId ? signUp : null;
}

/** This browser's connect sign-up for this account, whatever connect link it started from, or null. */
export function connectAiSignUpOf(storage: LocalStorageService, username: string | null | undefined,
                                  now: number = Date.now()): ConnectAiSignUp | null {
  const signUp = username ? readConnectAiSignUp(storage, now) : null;
  return signUp && sameLogin(signUp.username, username) ? signUp : null;
}

/**
 * True when this browser paid for this account's sign-up in the last 24 hours. The payment reaches LedgerSync a
 * little after the person is back, so until then the account still looks unpaid: the connect pages then wait
 * instead of offering the payment again. Keyed by the account, not the link, as clicking Connect again makes a new
 * link.
 */
export function hasRecentConnectAiPayment(storage: LocalStorageService, username: string | null | undefined,
                                          now: number = Date.now()): boolean {
  return isWithinMarkerAge(connectAiSignUpOf(storage, username, now)?.paidAt, now);
}
